import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { isChunkStaleError } from "@/components/AppResilience/error-classification";

type LazyModule<TProps extends object> = {
  readonly default: ComponentType<TProps>;
};

export interface LazyRetryOptions<TProps extends object> {
  readonly cacheBust?: () => Promise<LazyModule<TProps>>;
}

const RETRY_DELAYS_MS = [500, 1_500] as const;

export function lazyWithRetry<TProps extends object>(
  loader: () => Promise<LazyModule<TProps>>,
  options: LazyRetryOptions<TProps> = {},
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(() => loadWithRetry(loader, options));
}

async function loadWithRetry<TProps extends object>(
  loader: () => Promise<LazyModule<TProps>>,
  options: LazyRetryOptions<TProps>,
): Promise<LazyModule<TProps>> {
  for (const delayMs of RETRY_DELAYS_MS) {
    try {
      return await loader();
    } catch (error) {
      if (!isChunkStaleError(error)) throw error;
      await delay(delayMs);
    }
  }
  return options.cacheBust ? await options.cacheBust() : await loader();
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const __loadWithRetryForTest = loadWithRetry;
